import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Tag, Share2, BookOpen, ChevronRight, Printer, Link, Facebook, Twitter, MessageCircle, Mail } from '../lib/icons';
import { useBlogPostBySlug, useBlogTopicTags, useRelatedBlogPosts } from '../hooks/useBlog';
import { useTopicsList } from '../hooks/useTopics';
import { createUniqueSlug } from '../utils/slugUtils';
import { calculateReadingTime } from '../utils/readingTimeUtils';
import BlogTopicTagsDisplay from './BlogTopicTagsDisplay';
import { getBlogImage, BLOG_IMAGE_DEFAULT } from '../utils/imageUtils';
import SEO from './SEO';
import Breadcrumbs from './Breadcrumbs';
import { articleSchema, breadcrumbSchema } from '../lib/structuredData';

// Simple function to insert topic tags after first h2 paragraph
const insertTopicTagsAfterFirstH2 = (content: string, blogPostId: string): string => {
  if (!content || !blogPostId) return content;

  // Find the first h2 heading
  const h2Regex = /<h2[^>]*>.*?<\/h2>/i;
  const h2Match = content.match(h2Regex);
  
  if (!h2Match) {
    // If no h2 found, insert after first paragraph
    const pRegex = /<p[^>]*>.*?<\/p>/i;
    const pMatch = content.match(pRegex);
    
    if (pMatch) {
      const insertionPoint = content.indexOf(pMatch[0]) + pMatch[0].length;
      return content.slice(0, insertionPoint) + 
             `<div id="topic-tags-placeholder-${blogPostId}"></div>` + 
             content.slice(insertionPoint);
    }
    
    // If no paragraph found either, insert at the beginning
    return `<div id="topic-tags-placeholder-${blogPostId}"></div>` + content;
  }

  // Find the first paragraph after the h2
  const h2EndIndex = content.indexOf(h2Match[0]) + h2Match[0].length;
  const remainingContent = content.slice(h2EndIndex);
  const pRegex = /<p[^>]*>.*?<\/p>/i;
  const pMatch = remainingContent.match(pRegex);
  
  if (pMatch) {
    const pEndIndex = h2EndIndex + remainingContent.indexOf(pMatch[0]) + pMatch[0].length;
    return content.slice(0, pEndIndex) + 
           `<div id="topic-tags-placeholder-${blogPostId}"></div>` + 
           content.slice(pEndIndex);
  }
  
  // If no paragraph after h2, insert right after h2
  return content.slice(0, h2EndIndex) + 
         `<div id="topic-tags-placeholder-${blogPostId}"></div>` + 
         content.slice(h2EndIndex);
};

const BlogPost: React.FC = () => {
  // useBlogPostBySlug laedt nur den einen Post (vorher: useBlogPosts() →
  // alle 228 Posts inkl. full HTML-Content, ~1 MB Payload pro Detail-Visit).
  // useTopicsList() laedt 572 Topics nur Listen-Felder ohne content (~30 KB
  // statt 3.7 MB mit useTopics()).
  const { topics } = useTopicsList();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams<{ slug: string }>();
  const [showShareMenu, setShowShareMenu] = React.useState(false);

  // Check if user came from a topic detail page
  const fromTopicId = location.state?.fromTopicId;
  const fromSearch = location.state?.fromSearch;

  const { post: postRaw, loading, error } = useBlogPostBySlug(slug);
  const post = postRaw || undefined;
  const { blogTopicTags } = useBlogTopicTags(post?.id);
  const { posts: relatedPosts } = useRelatedBlogPosts(post?.category, post?.id, 3);

  // Effect to inject topic tags into placeholder after content is rendered
  React.useEffect(() => {
    if (!post?.id || !blogTopicTags || blogTopicTags.length === 0) return;

    const placeholder = document.getElementById(`topic-tags-placeholder-${post.id}`);
    if (!placeholder) return;

    // Filter active topics
    const activeTopicTags = blogTopicTags.filter(tag => tag.topic && tag.topic.is_active);
    if (activeTopicTags.length === 0) {
      placeholder.style.display = 'none';
      return;
    }

    // Create topic tags HTML
    const topicTagsHTML = activeTopicTags.map(tag => {
      const topic = tag.topic;
      const topicType = topic.topic_type || 'consultation';
      const slug = createUniqueSlug(topic.name, topic.id);
      
      let path = '/beratung/';
      if (topicType === 'disease') path = '/krankheiten/';
      if (topicType === 'symptom') path = '/symptome/';
      
      return `<a href="${path}${slug}" class="inline-flex items-center text-sm text-gray-700 hover:text-blue-600 transition-colors underline decoration-dotted hover:decoration-solid mr-4">${topic.name}</a>`;
    }).join('');

    placeholder.innerHTML = `
      <div class="border-l-4 border-gray-300 pl-4 my-6">
        <p class="text-sm text-gray-600 mb-3">
          <strong>Verwandte Themen:</strong>
        </p>
        <div class="flex flex-wrap gap-y-1">
          ${topicTagsHTML}
        </div>
      </div>
    `;

    // Add click handlers
    const links = placeholder.querySelectorAll('a');
    links.forEach((link, index) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const topic = activeTopicTags[index]?.topic;
        if (topic) {
          const slug = createUniqueSlug(topic.name, topic.id);
          const topicType = topic.topic_type || 'consultation';
          const navigationState = { fromBlogPostId: post.id };
          
          switch (topicType) {
            case 'disease':
              navigate(`/krankheiten/${slug}`, { state: navigationState });
              break;
            case 'symptom':
              navigate(`/symptome/${slug}`, { state: navigationState });
              break;
            case 'consultation':
            default:
              navigate(`/beratung/${slug}`, { state: navigationState });
              break;
          }
        }
      });
    });
  }, [post?.id, blogTopicTags, navigate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handlePrint = () => {
    // Erstelle Print-Styles für die aktuelle Seite
    const printStyles = `
      <style id="print-styles">
        @media print {
          /* Verstecke alle Elemente außer Print-Content */
          body > *:not(.print-content) {
            display: none !important;
          }
          
          body {
            margin: 0;
            padding: 0;
            background: white;
            color: black;
          }
          
          .print-content {
            display: block !important;
            position: static !important;
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            font-family: 'Times New Roman', Times, serif !important;
            font-size: 12pt !important;
            line-height: 1.5 !important;
            color: #000 !important;
            background: white !important;
          }
          
          .print-title {
            font-size: 18pt;
            font-weight: bold;
            margin-bottom: 20pt;
            page-break-after: avoid;
          }
          
          .print-meta {
            font-size: 10pt;
            color: #666;
            margin-bottom: 20pt;
            padding: 8pt;
            border: 1px solid #ddd;
            background: #f9f9f9;
            page-break-inside: avoid;
          }
          
          .print-content-body {
            font-size: 12pt;
            line-height: 1.5;
          }
          
          .print-content-body h2 {
            font-size: 14pt;
            font-weight: bold;
            margin: 16pt 0 8pt 0;
            page-break-after: avoid;
          }
          
          .print-content-body h3 {
            font-size: 12pt;
            font-weight: bold;
            margin: 12pt 0 6pt 0;
            page-break-after: avoid;
          }
          
          .print-content-body p {
            margin: 0 0 12pt 0;
            orphans: 2;
            widows: 2;
          }
          
          .print-content-body ul, .print-content-body ol {
            margin: 0 0 12pt 0;
            padding-left: 20pt;
          }
          
          .print-content-body li {
            margin: 0 0 6pt 0;
          }
          
          .print-footer {
            margin-top: 30pt;
            padding-top: 15pt;
            border-top: 1px solid #ddd;
            font-size: 10pt;
            color: #666;
            text-align: center;
            page-break-inside: avoid;
          }
          
          .print-url {
            font-family: 'Courier New', monospace;
            font-size: 9pt;
            word-break: break-all;
          }
          
          @page {
            margin: 2cm;
            size: A4;
            orphans: 2;
            widows: 2;
          }
        }
        
        @media screen {
          .print-content {
            display: none;
          }
        }
      </style>
    `;
    
    // Füge Print-Styles zum Head hinzu
    const existingStyles = document.getElementById('print-styles');
    if (existingStyles) {
      existingStyles.remove();
    }
    document.head.insertAdjacentHTML('beforeend', printStyles);
    
    // Erstelle Print-Content
    const printContent = `
      <div class="print-content">
        <h1 class="print-title">${post?.title || ''}</h1>
        
        <div class="print-meta">
          <strong>Autor:</strong> ${post?.author || ''}<br>
          <strong>Veröffentlicht:</strong> ${formatDate(post?.published_at || post?.created_at || '')}<br>
          <strong>Kategorie:</strong> ${post?.category || ''}<br>
          <strong>Lesezeit:</strong> ${calculateReadingTime(post?.content || '')}
        </div>
        
        <div class="print-content-body">
          ${post?.content
            ?.replace(/<img[^>]*>/gi, '') // Entferne Bilder
            ?.replace(/<button[^>]*>.*?<\/button>/gi, '') // Entferne Buttons
            ?.replace(/<a[^>]*>(.*?)<\/a>/gi, '$1') // Entferne Links, behalte Text
            ?.replace(/<script[^>]*>.*?<\/script>/gi, '') // Entferne Scripts
            ?.replace(/<style[^>]*>.*?<\/style>/gi, '') // Entferne Style-Tags
            ?.replace(/class="[^"]*"/gi, '') // Entferne CSS-Klassen
            ?.replace(/style="[^"]*"/gi, '') // Entferne Inline-Styles
            ?.replace(/<div[^>]*>/gi, '') // Entferne div-Tags
            ?.replace(/<\/div>/gi, '') // Entferne schließende div-Tags
            ?.replace(/<span[^>]*>/gi, '') // Entferne span-Tags
            ?.replace(/<\/span>/gi, '') // Entferne schließende span-Tags
            || 'Kein Inhalt verfügbar'}
        </div>
        
        <div class="print-footer">
          <p><strong>Ein Artikel von Behandlung.de</strong></p>
          <p>Ihre vertrauenswürdige Plattform für Gesundheitsinformationen</p>
          <p class="print-url">${window.location.href}</p>
        </div>
      </div>
    `;
    
    // Füge Print-Content zum Body hinzu
    const existingPrintContent = document.querySelector('.print-content');
    if (existingPrintContent) {
      existingPrintContent.remove();
    }
    document.body.insertAdjacentHTML('beforeend', printContent);
    
    // Starte Druckvorgang
    setTimeout(() => {
      window.print();
      
      // Cleanup nach dem Drucken
      setTimeout(() => {
        const printContentElement = document.querySelector('.print-content');
        const printStylesElement = document.getElementById('print-styles');
        if (printContentElement) printContentElement.remove();
        if (printStylesElement) printStylesElement.remove();
      }, 1000);
    }, 100);
  };

  const handleShare = (platform?: string) => {
    const url = window.location.href;
    const title = post?.title || '';
    const text = post?.excerpt || '';

    if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
    } else if (platform === 'whatsapp') {
      const whatsappText = `${title}\n\n${text}\n\n${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(whatsappText)}`, '_blank');
    } else if (platform === 'email') {
      // E-Mail-Vorlage direkt in Zwischenablage kopieren (zuverlässiger)
      const subject = `Interessanter Artikel: ${title}`;
      const body = `Hallo,\n\nich möchte diesen interessanten Artikel mit dir teilen:\n\n${title}\n\n${text}\n\nHier kannst du den vollständigen Artikel lesen:\n${url}\n\nViele Grüße`;
      const emailText = `Betreff: ${subject}\n\n${body}`;
      
      if (navigator.clipboard) {
        navigator.clipboard.writeText(emailText).then(() => {
          alert('📧 E-Mail-Vorlage wurde in die Zwischenablage kopiert!\n\nÖffnen Sie Ihren E-Mail-Client und fügen Sie den Text ein (Cmd+V / Strg+V).');
        }).catch(() => {
          // Fallback für Clipboard-Probleme
          copyToClipboardFallback(emailText);
        });
      } else {
        // Fallback für ältere Browser
        copyToClipboardFallback(emailText);
      }
      
      function copyToClipboardFallback(text: string) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          alert('📧 E-Mail-Vorlage wurde in die Zwischenablage kopiert!\n\nÖffnen Sie Ihren E-Mail-Client und fügen Sie den Text ein (Cmd+V / Strg+V).');
        } catch (err) {
          alert('❌ Kopieren fehlgeschlagen. Bitte kopieren Sie den Link manuell:\n\n' + url);
        }
        document.body.removeChild(textArea);
      }
    } else if (platform === 'copy') {
      navigator.clipboard.writeText(url).then(() => {
        alert('Link wurde in die Zwischenablage kopiert!');
      }).catch(() => {
        // Fallback für ältere Browser
        const textArea = document.createElement('textarea');
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Link wurde in die Zwischenablage kopiert!');
      });
    } else if (navigator.share) {
      // Native Web Share API
      navigator.share({
        title: title,
        text: text,
        url: url
      }).catch((error) => {
        console.log('Error sharing:', error);
        // Fallback: copy to clipboard
        handleShare('copy');
      });
    } else {
      // Fallback: copy to clipboard
      handleShare('copy');
    }
    setShowShareMenu(false);
  };

  if (loading) {
    return (
      <article className="py-8 bg-gray-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Lade Artikel...</p>
          </div>
        </div>
      </article>
    );
  }

  if (error) {
    return (
      <article className="py-8 bg-gray-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <p className="text-red-600">Fehler beim Laden des Artikels: {error}</p>
            <button
              onClick={() => navigate('/wissen')}
              className="mt-4 text-blue-600 hover:text-blue-800"
            >
              Zurück zum Wissen
            </button>
          </div>
        </div>
      </article>
    );
  }

  if (!post) {
    return (
      <section className="py-8 bg-gray-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Artikel nicht gefunden</h1>
            <button
              onClick={() => navigate('/wissen')}
              className="text-blue-600 hover:text-blue-800"
            >
              Zurück zum Wissen
            </button>
          </div>
        </div>
      </section>
    );
  }

  const postTypeLabel = post.post_type === 'fachbeitrag' ? 'Fachbeitrag' : post.post_type === 'news' ? 'News' : 'Artikel';
  const seoTitle = `${post.title} - ${postTypeLabel}`;
  const seoDescription = post.excerpt || `${post.title}. Lesen Sie mehr in unserem ${postTypeLabel}.`;

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={[
          ...post.tags,
          postTypeLabel,
          'Gesundheit',
          'Wissen',
          'Ratgeber'
        ]}
        type="article"
        author={post.author}
        publishedTime={post.published_at || post.created_at}
        modifiedTime={post.updated_at || post.published_at || post.created_at}
        ogImage={post.image_url}
        structuredData={[
          articleSchema({
            headline: post.title,
            description: post.excerpt || seoDescription,
            url: `/wissen/${slug}`,
            image: post.image_url,
            datePublished: post.published_at || post.created_at,
            dateModified: post.updated_at || post.published_at || post.created_at,
            authorName: post.author,
          }),
          breadcrumbSchema([
            { name: 'Startseite', url: '/' },
            { name: 'Wissen', url: '/wissen' },
            { name: post.title, url: `/wissen/${slug}` },
          ]),
        ]}
      />
      <article className="py-8 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => {
            if (fromSearch) {
              navigate('/search');
            } else if (fromTopicId) {
              // Navigate back to the specific topic detail page
              const topic = topics.find(t => t.id === fromTopicId);
              if (topic) {
                const slug = createUniqueSlug(topic.name, topic.id);
                const topicType = (topic as any).topic_type || 'consultation';
                
                switch (topicType) {
                  case 'disease':
                    navigate(`/krankheiten/${slug}`);
                    break;
                  case 'symptom':
                    navigate(`/symptome/${slug}`);
                    break;
                  case 'consultation':
                  default:
                    navigate(`/beratung/${slug}`);
                    break;
                }
              } else {
                navigate('/wissen');
              }
            } else {
              navigate('/wissen');
            }
          }}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {fromSearch ? 'Zurück zu den Suchergebnissen' : fromTopicId ? 'Zurück zum Thema' : 'Zurück zum Wissen'}
        </button>

        <Breadcrumbs
          items={[
            { name: 'Startseite', href: '/' },
            { name: 'Wissen', href: '/wissen' },
            { name: post.title },
          ]}
          className="mb-6"
        />

        {/* Compact Article Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 leading-tight">
            {post.title}
          </h1>
          
          {/* Excerpt */}
          <p className="text-lg text-gray-600 mb-4 leading-relaxed">
            {post.excerpt}
          </p>

          {/* Compact Meta Information */}
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-2">
                <User className="w-4 h-4 text-white" />
              </div>
              <span className="font-medium">{post.author}</span>
            </div>
            
            <div className="flex items-center text-sm text-gray-500">
              <Calendar className="w-4 h-4 mr-1" />
              <span>{formatDate(post.published_at || post.created_at)}</span>
            </div>
            
            <div className="flex items-center text-sm text-gray-500">
              <Clock className="w-4 h-4 mr-1" />
              <span>{calculateReadingTime(post.content)}</span>
            </div>
            
            {/* Actions */}
            <div className="flex items-center space-x-3 ml-auto">
              <div className="relative">
                <button 
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-blue-600 transition-colors border border-gray-300 rounded-lg hover:border-blue-300"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Teilen
                </button>
                
                {showShareMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowShareMenu(false)}
                    />
                    <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                      <button
                        onClick={() => handleShare('facebook')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center rounded-t-lg"
                      >
                        <Facebook className="w-4 h-4 mr-2 text-blue-600" />
                        Facebook
                      </button>
                      <button
                        onClick={() => handleShare('twitter')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <Twitter className="w-4 h-4 mr-2 text-blue-400" />
                        Twitter
                      </button>
                      <button
                        onClick={() => handleShare('whatsapp')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <MessageCircle className="w-4 h-4 mr-2 text-green-600" />
                        WhatsApp
                      </button>
                      <button
                        onClick={() => handleShare('email')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <Mail className="w-4 h-4 mr-2 text-gray-600" />
                        E-Mail
                      </button>
                      <div className="border-t border-gray-200 my-1"></div>
                      <button
                        onClick={() => handleShare('copy')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center rounded-b-lg"
                      >
                        <Link className="w-4 h-4 mr-2 text-gray-600" />
                        Link kopieren
                      </button>
                    </div>
                  </>
                )}
              </div>
              
              <button 
                onClick={handlePrint}
                className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-green-600 transition-colors border border-gray-300 rounded-lg hover:border-green-300"
              >
                <Printer className="w-4 h-4 mr-1" />
                Drucken
              </button>
            </div>
          </div>

          {/* Compact Tags */}
          <div className="flex flex-wrap gap-2">
            {/* Category and Type Badges */}
            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full font-medium">
              {post.category}
            </span>
            <span className={`px-3 py-1 text-sm rounded-full ${
              post.post_type === 'news' 
                ? 'bg-blue-100 text-blue-800'
                : post.post_type === 'fachbeitrag'
                ? 'bg-purple-100 text-purple-800'
                : 'bg-green-100 text-green-800'
            }`}>
              {post.post_type === 'artikel' ? 'Artikel' : 
               post.post_type === 'news' ? 'News' : 'Fachbeitrag'}
            </span>
            
            {/* Regular Tags */}
            {post.tags && post.tags.length > 0 && post.tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full flex items-center">
                <Tag className="w-3 h-3 mr-1" />
                {tag}
              </span>
            ))}
            {post.tags && post.tags.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
                +{post.tags.length - 3} weitere
              </span>
            )}
          </div>
        </div>
        {/* Featured Image — fetchpriority high markiert es als LCP-Kandidat,
            damit der Browser es bevorzugt vor anderen Ressourcen laedt.
            Vorher zeigte PSI 1.3s Resource-Load-Delay fuer Magazin-Posts. */}
        <div className="mb-6">
          <img
            src={getBlogImage(post.image_url)}
            alt={post.title}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            width={BLOG_IMAGE_DEFAULT.width}
            height={BLOG_IMAGE_DEFAULT.height}
            onError={(e) => { e.currentTarget.src = getBlogImage(undefined); }}
            className="w-full h-64 sm:h-80 object-cover rounded-lg shadow-sm"
          />
        </div>

        {/* Main Content Container */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          {/* Article Content */}
          <div
            className="blog-content prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
          
          {/* Topic Tags at the end of the article content */}
          <BlogTopicTagsDisplay blogPostId={post.id} className="mt-8 pt-6 border-t border-gray-200" />
        </div>

        {/* Author Bio - only if author_bio exists */}
        {post.author_bio && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Über den Autor</h3>
            <div className="flex items-start space-x-3">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-medium text-gray-900 mb-1">{post.author}</p>
                <p className="text-gray-600 text-sm leading-relaxed">{post.author_bio}</p>
              </div>
            </div>
          </div>
        )}

        {/* Related Articles */}
        {relatedPosts.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ähnliche Artikel</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <article
                  key={relatedPost.id}
                  className="bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer border border-gray-200 overflow-hidden"
                  onClick={() => {
                    // Scroll to top when clicking related post
                    window.scrollTo(0, 0);
                   const slug = createUniqueSlug(relatedPost.title, relatedPost.id);
                   navigate(`/wissen/${slug}`);
                  }}
                >
                  <img
                    src={getBlogImage(relatedPost.image_url)}
                    alt={relatedPost.title}
                    loading="lazy"
                    decoding="async"
                    width={BLOG_IMAGE_DEFAULT.width}
                    height={BLOG_IMAGE_DEFAULT.height}
                    onError={(e) => { e.currentTarget.src = getBlogImage(undefined); }}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full mb-2 inline-flex items-center">
                      <Tag className="w-3 h-3 mr-1" />
                      {relatedPost.category}
                    </span>
                    <h4 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2">
                      {relatedPost.title}
                    </h4>
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-3 h-3 mr-1" />
                      {calculateReadingTime(relatedPost.content)}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* Call to Action - only show if blog post has topic tags */}
        {blogTopicTags && blogTopicTags.length > 0 && (
          <div className="bg-blue-50 rounded-lg p-6 text-center">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              Haben Sie Fragen zu diesem Thema?
            </h3>
            <p className="text-gray-600 mb-4">
              Finden Sie qualifizierte Ärzte in Ihrer Nähe und vereinbaren Sie einen Termin.
            </p>
            <button
              onClick={() => {
                // Get topic IDs from blog post tags
                const topicIds = blogTopicTags?.map(tag => tag.topic_id) || [];
                
                if (topicIds.length > 0) {
                  // Navigate to doctors page with topic filters
                  navigate('/anbieter', { 
                    state: { 
                      topicFilters: topicIds,
                      fromBlogPost: post?.id,
                      blogPostTitle: post?.title
                    } 
                  });
                } else {
                  // Fallback to regular doctors page
                  navigate('/anbieter');
                }
              }}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center mx-auto"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              Ärzte finden
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        )}
      </div>
    </article>
    </>
  );
};

export default BlogPost;